// src/components/MyAppBar.js
//
// App bar, rebuilt to design handoff option 6a ("Grouped top bar + Settings as
// a three-column panel").
//
// The old bar carried nine destinations plus search, help, a role chip and a
// logout button, and wrapped on a laptop (#245). The fix is grouping, not
// shrinking: the daily destinations stay flat (Tasks, Assignments, Assets,
// Issues, and Analytics for admins and managers — each shown only where the
// plan and the workspace's switches include it), the two "go and find it
// later" views move under **Archive ▾**, and
// Settings leaves the nav row entirely — it is a gear on the right that opens a
// 580px panel with the 18 pages sorted into People & places / Templates /
// Platform, instead of an 18-item scrolling menu. The avatar carries profile,
// workspace switch and logout.
import React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Popover from '@mui/material/Popover';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import useIssueAlerts from '../hooks/useIssueAlerts';
import useNewTaskNotifications from '../hooks/useNewTaskNotifications';
import SubscriberSwitcher from './SubscriberSwitcher';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  Help as HelpIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  RestoreFromTrash as RecycleBinIcon,
  Download as DownloadIcon,
  SwapHoriz as SwitchIcon,
  Visibility as VisibilityIcon,
  NotificationsActive as NotificationsIcon,
} from '@mui/icons-material';
import GlobalSearch from './GlobalSearch';
import brandIcon from '../assets/traklog-icon-refined.svg';
import { visibleSettingsGroups } from './settingsMenu';
import { SYSTEM_ADMIN_FLAT, SYSTEM_ADMIN_GROUPS } from './systemAdminMenu';
import { TRAK, LABEL_SX } from '../theme/trakTokens';
import { initialLetters } from '../utils/workspaceCard';

// Roles a system admin can clamp the UI to. Operator and customer are absent on
// purpose: they are separate apps (the mobile client and the customer portal),
// not a narrower view of this one, so pretending to render them here would show
// something no operator or customer ever sees.
const VIEW_AS_ROLES = [
  { value: null, label: 'Yourself (system admin)' },
  { value: 'admin', label: 'Workspace admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'user', label: 'User' },
];

// Nav item as a 12.5px pill: active gets a translucent white fill, the rest sit
// at 72% so the current page reads at a glance on the ink-green bar.
const navSx = (active) => ({
  minWidth: 0,
  // A pill is one line or it is nothing: without this the label wraps under
  // pressure ("Live tasks" over two lines) and the row keeps claiming to fit
  // while it is already past its shrink limit.
  whiteSpace: 'nowrap',
  // …and it must not shrink either, or the nowrap label simply overflows its
  // own button and prints on top of the next one — which is what a phone got
  // ("TasksAssignmentsArchive" in one smear). Refusing to shrink is what makes
  // the deficit reach the scroll container below instead.
  flexShrink: 0,
  // The dropdown caret is a text glyph, but MUI sizes an endIcon's first child
  // through `.MuiButton-endIcon > *` — specificity the plain `fontSize: 9` on
  // that span never beat, so every "▾" in the bar rendered as a 20px triangle.
  '& .MuiButton-endIcon': { ml: 0.4 },
  '& .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: 9 },
  px: { xs: 1.15, sm: 1.5 },
  py: 1,
  borderRadius: '6px',
  font: `500 12.5px/1 ${TRAK.sans}`,
  color: active ? '#fff' : 'rgba(255,255,255,.72)',
  bgcolor: active ? 'rgba(255,255,255,.15)' : 'transparent',
  '&:hover': { bgcolor: 'rgba(255,255,255,.18)' },
});

// The nav row yields last and never disappears. Below roughly a laptop the
// search box has already shrunk to its floor and there is still not enough
// width for every destination — at which point the row scrolls inside the bar
// instead of pushing the document wider, so every page stays reachable. The
// scrollbar is hidden because a 3px trough inside a 54px bar reads as damage;
// swipe, or shift+wheel, does it.
// On a phone the row is always past the edge, and with the scrollbar hidden a
// destination cut mid-word ("Archiv") reads as a rendering fault rather than as
// "swipe for more". The mask fades the last few pixels so the cut is legible as
// an edge. It is only there below sm: on a laptop the row usually fits, and a
// gradient over nothing is just a smudge.
const NAV_FADE = 'linear-gradient(to right, #000 calc(100% - 26px), transparent)';
const NAV_ROW_SX = {
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  maskImage: { xs: NAV_FADE, md: 'none' },
  WebkitMaskImage: { xs: NAV_FADE, md: 'none' },
  // On a phone there is no width left for it beside the workspace name and the
  // right-hand cluster — it was down to about 100px, a sliver you would have to
  // swipe to reach anything past Tasks. `order` + a full-width basis drop it
  // onto a second line of the same wrapping toolbar, so it scrolls across the
  // whole screen. One instance, moved by CSS: rendering a phone copy as well
  // would give the Archive menu two anchors sharing one open state.
  order: { xs: 3, md: 0 },
  flexBasis: { xs: '100%', md: 'auto' },
  // Bleed to the screen edges so the fade sits at the edge, not inside a gutter.
  mx: { xs: -1.25, sm: -2.25, md: 0 },
  px: { xs: 1.25, sm: 2.25, md: 0 },
};

// One line, cut with an ellipsis rather than wrapped. Anything in the bar
// carrying a name someone else chose — a workspace, a person — needs this: a
// long one otherwise wraps and drags the 54px row taller.
const LINE_CLAMP = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const roundButtonSx = (active) => ({
  width: 30,
  height: 30,
  // A 30px square is already the floor; let it shrink and the icon inside it
  // goes oval when the row is tight.
  flexShrink: 0,
  borderRadius: '7px',
  bgcolor: active ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.10)',
  color: active ? '#fff' : 'rgba(255,255,255,.8)',
  '&:hover': { bgcolor: 'rgba(255,255,255,.26)' },
});

function MyAppBar({ currentUser, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  // The public issue-report page (/r/:code) is opened by a stranger scanning
  // an asset's QR label with their own phone — they were never asked to sign
  // in, and a "Login" link on a page built for them to report a problem fast
  // reads as admin chrome that leaked onto the public page.
  const isPublicIssueReportRoute = location.pathname.startsWith('/r/');
  // Tasks, Assignments, Assets, Issues and Archive are all subscriber-scoped.
  // The system-admin section is not in a subscriber at all, so offering them
  // there put five buttons belonging to the last-selected workspace under a
  // heading that says Platform. The subscriber underneath stays selected —
  // leaving the section, or picking a workspace from the switcher, brings the
  // row straight back with no state to restore.
  const inSystemAdmin = location.pathname.startsWith('/system-admin');
  // A user sent here has to choose a new password before anything else. The
  // token still carries the workspace from before, so every link in this bar
  // opened a working page and the reset could be walked around. Only the
  // avatar menu's Log out stays.
  const resettingPassword = location.pathname === '/force-password-reset';
  const {
    userRole, subscriberRole: ctxSubscriberRole, isSystemAdmin,
    simulatedRole, setSimulatedRole, subscriberId,
    accessibleSubscribers, customerAccessEntries,
  } = useAuth();
  // Same rule as the workspace dropdown (SubscriberSwitcher): with one place to
  // be, "Switch workspace" leads to a picker holding only the current one.
  const canSwitchWorkspace = isSystemAdmin
    || (accessibleSubscribers || []).length + (customerAccessEntries || []).length > 1;
  // Every button in the row below opens a subscriber-scoped page, so without a
  // workspace selected the row is five links to an empty shell. That is not
  // hypothetical: the last admin of a purged workspace was left signed in with
  // no workspace, and this row is what let them walk into a fully-rendered
  // Tasks page reporting 0 of 0 — the most convincing wrong answer the app
  // could have given them. A system admin legitimately has no workspace
  // selected, but `inSystemAdmin` already hides the row for them, and the
  // switcher is how they pick one.
  const hasWorkspace = Boolean(subscriberId);
  // On a page of a workspace: one is selected and this is not the platform
  // section. What the search field and the settings cog need to mean anything.
  const inWorkspace = hasWorkspace && !inSystemAdmin;
  const {
    hasIssueTracking, hasAssetTracking, hasFullTextSearch, hasAssignments, hasMassExport,
    hasAdvancedAnalytics, featureFlags, loading: featureLoading,
  } = useFeatureAccess();
  const { breachingCount } = useIssueAlerts();
  const taskNotifications = useNewTaskNotifications();
  const [settingsAnchor, setSettingsAnchor] = React.useState(null);
  const [archiveAnchor, setArchiveAnchor] = React.useState(null);
  const [avatarAnchor, setAvatarAnchor] = React.useState(null);
  const [sysAdminAnchor, setSysAdminAnchor] = React.useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);

  // Context first: it carries the "view as" clamp, and localStorage is only the
  // cold-start seed. Reading storage directly meant the app bar could not see a
  // simulated role — and went stale after a workspace switch.
  const subscriberRole = ctxSubscriberRole || localStorage.getItem('subscriberRole') || 'user';
  const canAccessSettings = () => subscriberRole === 'admin' || subscriberRole === 'manager';

  // Every flag, not a hand-picked four: an item gated on a flag this list did
  // not carry was hidden for everyone, silently. `featureFlags` is memoised in
  // the hook, so this only recomputes when the subscriber snapshot changes.
  const settingsGroups = React.useMemo(
    () => visibleSettingsGroups(subscriberRole, featureFlags),
    [subscriberRole, featureFlags]
  );

  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(/[.\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => initialLetters(part)[0]?.toUpperCase())
    .join('') || 'U';
  const roleLabel = userRole === 'customer'
    ? 'Customer'
    : subscriberRole.charAt(0).toUpperCase() + subscriberRole.slice(1);

  const closeAll = () => {
    setSettingsAnchor(null);
    setArchiveAnchor(null);
    setAvatarAnchor(null);
    setSysAdminAnchor(null);
  };

  const handleLogoutConfirm = async () => {
    try {
      setShowLogoutDialog(false);
      if (onLogout) await onLogout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  return (
    <AppBar position="sticky" elevation={0} sx={{ bgcolor: TRAK.green }}>
      {/* overflowX: 'clip' is the backstop, not the fix — the row is sized to
          fit below. It matters because the AppBar is sticky and therefore only
          as wide as the viewport: anything spilling out of this row extends the
          document's scroll width instead, and scrolling sideways then slides
          the green bar away and leaves bare white beside it. `clip` rather than
          `hidden` so the toolbar never becomes a scroll container (the menus
          and tooltips are portalled, so nothing legitimately paints outside). */}
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 54,
          px: { xs: 1.25, sm: 2.25 },
          gap: { xs: 1, sm: 2.5 },
          overflowX: 'clip',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          rowGap: { xs: 0.5, md: 0 },
          py: { xs: 0.75, md: 0 },
        }}
      >
        {/* Brand + workspace */}
        {currentUser ? (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.1, flexShrink: 0,
              pr: { xs: 1, sm: 2.25 },
              borderRight: { xs: 'none', sm: '1px solid rgba(255,255,255,.16)' },
            }}
          >
            <Box component="img" src={brandIcon} alt="TrakLog" width={22} height={22} sx={{ display: 'block' }} />
            {!resettingPassword && <SubscriberSwitcher />}
          </Box>
        ) : (
          <Typography variant="h6" component={RouterLink} to="/" sx={{ color: 'inherit', textDecoration: 'none', mr: 4 }}>
            TrakLog
          </Typography>
        )}

        {/* A customer has no nav row, so nothing pushes the right-hand cluster
            over on its own. The system-admin section has its own row below. */}
        {currentUser && (userRole === 'customer' || resettingPassword) && <Box sx={{ flexGrow: 1 }} />}

        {/* Platform nav. The subscriber-scoped row above is wrong here (this
            section is not in a workspace), but leaving nothing meant the only
            way between these twelve pages was the dashboard's Quick Actions —
            reachable only by going back to the dashboard first. */}
        {currentUser && inSystemAdmin && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexGrow: 1, ...NAV_ROW_SX }}>
            {SYSTEM_ADMIN_FLAT.map(({ path, label, exact }) => (
              <Button
                key={path}
                component={RouterLink}
                to={path}
                sx={navSx(exact ? location.pathname === path : isActive(path))}
              >
                {label}
              </Button>
            ))}

            {SYSTEM_ADMIN_GROUPS.map(group => {
              const open = sysAdminAnchor?.group === group.title;
              return (
                <React.Fragment key={group.title}>
                  <Button
                    onClick={(e) => setSysAdminAnchor({ el: e.currentTarget, group: group.title })}
                    endIcon={<Box component="span" sx={{ fontSize: 9, opacity: 0.6 }}>▾</Box>}
                    sx={navSx(group.items.some(i => isActive(i.path)))}
                  >
                    {group.title}
                  </Button>
                  <Menu
                    anchorEl={sysAdminAnchor?.el}
                    open={open}
                    onClose={() => setSysAdminAnchor(null)}
                    slotProps={{ paper: { sx: { width: 210, mt: 1 } } }}
                  >
                    {group.items.map(({ path, label, icon: Icon }) => (
                      <MenuItem
                        key={path}
                        component={RouterLink}
                        to={path}
                        selected={isActive(path)}
                        onClick={closeAll}
                        sx={{ py: 1.1 }}
                      >
                        <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                        <ListItemText primary={label} />
                      </MenuItem>
                    ))}
                  </Menu>
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {currentUser && userRole !== 'customer' && !inSystemAdmin && hasWorkspace && !resettingPassword && (
          <Box data-tour="nav-bar" sx={{ display: 'flex', alignItems: 'center', gap: '2px', flexGrow: 1, ...NAV_ROW_SX }}>
            <Button component={RouterLink} to="/tasks" sx={navSx(isActive('/tasks'))}>Tasks</Button>
            {!featureLoading && hasAssignments && (
              <Button component={RouterLink} to="/assignments" sx={navSx(isActive('/assignments'))}>Assignments</Button>
            )}
            {!featureLoading && hasAssetTracking && (
              <Button component={RouterLink} to="/assets" sx={navSx(isActive('/assets'))}>Assets</Button>
            )}
            {!featureLoading && hasIssueTracking && (
              <Button component={RouterLink} to="/issues" sx={navSx(isActive('/issues'))}>
                Issues
                {/* Red is reserved for a missed SLA — an open issue that is
                    still inside its window is not something to interrupt for. */}
                {breachingCount > 0 && (
                  <Box
                    component="span"
                    aria-label={`${breachingCount} overdue issues`}
                    sx={{
                      ml: 0.75, px: '5px', py: '2px', borderRadius: '8px',
                      bgcolor: TRAK.danger, color: '#fff', font: `600 9.5px/1 ${TRAK.mono}`,
                    }}
                  >
                    {breachingCount}
                  </Box>
                )}
              </Button>
            )}
            {/* Pro analytics dashboard. Admin / manager only, matching the
                route guard and the API — a plain user would only be refused. */}
            {!featureLoading && hasAdvancedAnalytics && canAccessSettings() && (
              <Button component={RouterLink} to="/analytics" sx={navSx(isActive('/analytics'))}>Analytics</Button>
            )}

            {/* Archive — the two views you go and find something in */}
            <Button
              onClick={(e) => setArchiveAnchor(e.currentTarget)}
              endIcon={<Box component="span" sx={{ fontSize: 9, opacity: 0.6 }}>▾</Box>}
              sx={navSx(isActive('/recycle-bin') || isActive('/downloads'))}
            >
              Archive
            </Button>
            <Menu
              anchorEl={archiveAnchor}
              open={Boolean(archiveAnchor)}
              onClose={() => setArchiveAnchor(null)}
              slotProps={{ paper: { sx: { width: 210, mt: 1 } } }}
            >
              <MenuItem component={RouterLink} to="/recycle-bin" onClick={closeAll} sx={{ py: 1.1 }}>
                <ListItemIcon><RecycleBinIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Recycle bin" />
              </MenuItem>
              {hasMassExport && (
                <MenuItem component={RouterLink} to="/downloads" onClick={closeAll} sx={{ py: 1.1 }}>
                  <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Downloads" />
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}

        {currentUser ? (
          // minWidth: 0 so this cluster is the one that yields when the bar is
          // tight — the search inside it shrinks to a floor, the nav row keeps
          // every destination readable. Without it the deficit was split
          // between the two and both lost.
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
              flexShrink: { xs: 0, sm: 1 },
              ml: { xs: 'auto', md: 0 },
              // Between sm and md the toolbar wraps, and wrapping is decided on
              // an item's *content* width — the search field's natural ~300px
              // was enough to send this whole cluster onto a line of its own,
              // giving a three-row bar. Asking for nothing and growing into
              // what line 1 has left keeps it beside the workspace name; the
              // field's own 140px floor still protects it.
              flexGrow: { xs: 0, sm: 1, md: 0 },
              flexBasis: { xs: 'auto', sm: 0, md: 'auto' },
            }}
          >
            {/* Search and the settings cog belong to a workspace, like the row
                of links above. On the system-admin pages they acted on the
                workspace that was open last, under a heading that says
                Platform: "123" found 8,845 of one customer's tasks there
                (product owner, 2026-10-13). */}
            {!featureLoading && hasFullTextSearch && userRole !== 'customer' && !resettingPassword
              && inWorkspace && <GlobalSearch />}

            {/* Duplicated by "User guide" in the avatar menu, so on a phone —
                where every 30px shows — the menu is the only copy. */}
            {!resettingPassword && <Tooltip title="User guide">
              <IconButton
                component={RouterLink}
                to="/settings/user-guide"
                aria-label="User guide"
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, ...roundButtonSx(isActive('/settings/user-guide')) }}
              >
                <HelpIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>}

            {canAccessSettings() && userRole !== 'customer' && !resettingPassword && inWorkspace && (
              <Box data-tour="settings-menu" sx={{ display: 'inline-flex' }}>
                <Tooltip title="Settings">
                  <IconButton
                    onClick={(e) => setSettingsAnchor(e.currentTarget)}
                    sx={roundButtonSx(isActive('/settings'))}
                    aria-label="Settings"
                    aria-haspopup="menu"
                  >
                    <SettingsIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Avatar: identity, plus the things you do to your own session */}
            <Button
              onClick={(e) => setAvatarAnchor(e.currentTarget)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexShrink: 0,
                minWidth: 0, px: { xs: 0.5, sm: 0 },
                pl: { xs: 1, sm: 1.75 }, ml: { xs: 0, sm: 0.5 },
                borderLeft: { xs: 'none', sm: '1px solid rgba(255,255,255,.16)' }, borderRadius: 0,
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 28, height: 28, borderRadius: '50%', bgcolor: TRAK.accent,
                  font: `600 11px/28px ${TRAK.sans}`, color: TRAK.ink, textAlign: 'center', flex: 'none',
                }}
              >
                {initials}
              </Box>
              <Box sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' }, minWidth: 0, maxWidth: 140 }}>
                <Box sx={{ font: `500 12px/1.2 ${TRAK.sans}`, color: '#fff', ...LINE_CLAMP }}>{displayName}</Box>
                <Box sx={{ font: `500 9.5px/1.2 ${TRAK.sans}`, color: TRAK.onDarkChipText }}>{roleLabel}</Box>
              </Box>
              <Box component="span" sx={{ fontSize: 9, color: 'rgba(255,255,255,.55)', display: { xs: 'none', sm: 'block' } }}>▾</Box>
            </Button>
            <Menu
              anchorEl={avatarAnchor}
              open={Boolean(avatarAnchor)}
              onClose={() => setAvatarAnchor(null)}
              slotProps={{ paper: { sx: { width: 250, mt: 1 } } }}
            >
              <Box sx={{ px: 1.5, py: 1 }}>
                <Typography sx={{ font: `600 12.5px/1.3 ${TRAK.sans}`, color: TRAK.text }}>{displayName}</Typography>
                <Typography sx={{ font: `400 11px/1.3 ${TRAK.sans}`, color: TRAK.muted }}>{currentUser.email}</Typography>
              </Box>
              <Divider />
              {canSwitchWorkspace && !resettingPassword && (
                <MenuItem component={RouterLink} to="/select-subscriber" onClick={closeAll} sx={{ py: 1.1 }}>
                  <ListItemIcon><SwitchIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Switch workspace" />
                </MenuItem>
              )}
              {!resettingPassword && (
                <MenuItem component={RouterLink} to="/settings/user-guide" onClick={closeAll} sx={{ py: 1.1 }}>
                  <ListItemIcon><HelpIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="User guide" />
                </MenuItem>
              )}
              {isSystemAdmin && [
                <Divider key="view-as-divider" />,
                <Box key="view-as" sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                  <Typography sx={{ font: `600 10px/1 ${TRAK.sans}`, color: TRAK.muted, letterSpacing: '.06em', textTransform: 'uppercase' }}>
                    View as
                  </Typography>
                </Box>,
                ...VIEW_AS_ROLES.map(role => (
                  <MenuItem
                    key={role.value}
                    selected={(simulatedRole || null) === role.value}
                    onClick={() => { setSimulatedRole(role.value); closeAll(); }}
                    sx={{ py: 0.75 }}
                  >
                    <ListItemIcon>
                      {(simulatedRole || null) === role.value
                        ? <VisibilityIcon fontSize="small" sx={{ color: TRAK.green }} />
                        : <Box sx={{ width: 20 }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={role.label}
                      slotProps={{ primary: { sx: { font: `400 12.5px/1.3 ${TRAK.sans}` } } }}
                    />
                  </MenuItem>
                )),
              ]}
              {taskNotifications.supported && [
                <Divider key="notify-divider" />,
                <MenuItem
                  key="notify"
                  onClick={(e) => {
                    // Requesting permission needs a user gesture, so it has to
                    // happen here rather than on mount. Keep the menu open so
                    // the switch can be seen to change, or to stay off if the
                    // browser refuses.
                    e.preventDefault();
                    taskNotifications.setEnabled(!taskNotifications.enabled);
                  }}
                  disabled={taskNotifications.blocked}
                  sx={{ py: 1.1 }}
                >
                  <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    sx={{ mr: 1.5 }}
                    primary="Notify me of new tasks"
                    secondary={taskNotifications.blocked
                      ? 'Blocked in your browser settings'
                      : undefined}
                    slotProps={{
                      primary: { sx: { font: `400 12.5px/1.3 ${TRAK.sans}` } },
                      secondary: { sx: { font: `400 10.5px/1.2 ${TRAK.sans}`, color: TRAK.muted } },
                    }}
                  />
                  <Switch
                    edge="end"
                    size="small"
                    checked={taskNotifications.enabled}
                    disabled={taskNotifications.blocked}
                    slotProps={{ input: { 'aria-label': 'Notify me of new tasks' } }}
                  />
                </MenuItem>,
              ]}
              <Divider />
              <MenuItem
                onClick={() => { closeAll(); setShowLogoutDialog(true); }}
                sx={{ py: 1.1, color: TRAK.dangerText }}
              >
                <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: TRAK.dangerText }} /></ListItemIcon>
                <ListItemText primary="Log out" />
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          !isPublicIssueReportRoute && <Button color="inherit" component={RouterLink} to="/login">Login</Button>
        )}
      </Toolbar>

      {/* Settings panel — three columns instead of an 18-item scroll */}
      <Popover
        open={Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 320, sm: 580 }, mt: 1, p: '16px 18px', borderRadius: '11px',
              border: `1px solid rgba(13,51,32,.12)`, boxShadow: '0 16px 44px rgba(13,51,32,.22)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1.5, mb: 1.75, borderBottom: `1px solid rgba(13,51,32,.08)` }}>
          <Typography sx={{ font: `600 13px/1 ${TRAK.sans}`, color: TRAK.ink }}>Settings</Typography>
          <Box
            component="span"
            sx={{
              px: 1, py: '3px', borderRadius: '11px', bgcolor: TRAK.successFill,
              font: `600 10px/1 ${TRAK.sans}`, color: TRAK.greenDark,
            }}
          >
            {roleLabel} view
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button
            component={RouterLink}
            to="/settings"
            onClick={closeAll}
            sx={{ minWidth: 0, font: `500 11.5px/1 ${TRAK.sans}`, color: TRAK.green }}
          >
            Overview →
          </Button>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 2.5 }}>
          {settingsGroups.map(group => (
            <Box key={group.title}>
              <Typography component="div" sx={{ ...LABEL_SX, mb: 1.1 }}>{group.title}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {group.items.map(item => (
                  <Box
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    onClick={closeAll}
                    sx={{
                      px: 1, py: 0.75, borderRadius: '6px', textDecoration: 'none',
                      font: `500 12.5px/1.3 ${TRAK.sans}`, color: TRAK.text,
                      bgcolor: isActive(item.path) ? '#f4fbf6' : 'transparent',
                      '&:hover': { bgcolor: '#f4fbf6' },
                    }}
                  >
                    {item.label}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 2, mt: 1.75, pt: 1.5,
            borderTop: `1px solid rgba(13,51,32,.08)`,
          }}
        >
          {[
            { path: '/settings/user-guide', label: 'User guide' },
            { path: '/settings/about', label: 'About', adminOnly: true },
          ]
            .filter(link => !link.adminOnly || subscriberRole === 'admin')
            .map(link => (
              <Box
                key={link.path}
                component={RouterLink}
                to={link.path}
                onClick={closeAll}
                sx={{ font: `500 12px/1 ${TRAK.sans}`, color: TRAK.muted, textDecoration: 'none', '&:hover': { color: TRAK.green } }}
              >
                {link.label}
              </Box>
            ))}
        </Box>
      </Popover>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out? You'll need to sign in again to access your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogoutDialog(false)} color="primary">Cancel</Button>
          <Button onClick={handleLogoutConfirm} color="primary" variant="contained" startIcon={<LogoutIcon />}>
            Log Out
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
}
export default MyAppBar;
